const DEFAULT_CONFIG = {
    endpoint: '/api/v2/client/error',
    enabled: true,
    maxPerMinute: 30,
    excludedPatterns: [
        /can't access property\s+["'][^"']+["']\s*,\s*href is undefined/i,
        /cannot read properties of undefined/i,
        /cannot read properties of null/i,
        /undefined is not a function/i,
        /NetworkError when attempting to fetch/i,
        /^Network Error$/i,
        /Unhandled error:\s+Failed to fetch/i,
        /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i,
        /cashbackreminder/i,
        /UET is not defined/i,
        /window\.webkit\.messageHandlers/i,
        /window\.ethereum/i,
        /__firefox__/i,
        /__gCrWeb/i,
        /DarkReader/i,
        /^Script error\.$/i,
        /chrome-extension:\/\//i,
        /moz-extension:\/\//i,
        /safari-web-extension:\/\//i,
        /webkit-masked-url:\/\//i,
        /^iabjs:\/\//i,
        /change value of a readonly property/i,
        /Java object is gone/i,
        /enableButtonsClickedMetaDataLogging/i,
        /Unable to preload CSS for/i,
        /Failed to fetch dynamically imported module/i,
        // Mobile Safari phrasing for a failed dynamic-chunk load: instead of
        // rejecting, it resolves the import() with `undefined`, so the module's
        // `.default` access throws "undefined is not an object (evaluating
        // '<var>.default')" (see #5256). Same stale post-deploy class as #5124;
        // it self-heals on the reload-once path in app.js, so drop the noise.
        /is not an object \(evaluating '[^']*\.default'\)/i,
        /Importing a module script failed/i,
        /Zahlungsmethode nicht authentifizieren/i,
        /args\.site\.enabledFeatures is undefined/i,
        /unable to authenticate your payment method/i,
    ],
};

let config = { ...DEFAULT_CONFIG };
let requestCount = 0;
let requestCounterStartedAt = Date.now();
let globalHandlersRegistered = false;

function resetRequestCounterIfNeeded() {
    if (Date.now() - requestCounterStartedAt > 60000) {
        requestCount = 0;
        requestCounterStartedAt = Date.now();
    }
}

function isAllowedToSend() {
    if (!config.enabled) {
        return false;
    }

    resetRequestCounterIfNeeded();
    if (requestCount >= config.maxPerMinute) {
        return false;
    }

    requestCount += 1;
    return true;
}

function normalizeError(error) {
    if (error instanceof Error) {
        return {
            message: error.message || 'Unknown frontend error',
            stack: error.stack || null,
        };
    }

    if (typeof error === 'string') {
        return {
            message: error,
            stack: null,
        };
    }

    if (error && typeof error === 'object') {
        return {
            message: error.message || JSON.stringify(error),
            stack: error.stack || null,
        };
    }

    return {
        message: 'Unknown frontend error',
        stack: null,
    };
}

function sanitizeMeta(meta) {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
        return null;
    }

    try {
        const serialized = JSON.stringify(meta);
        if (serialized.length > 10000) {
            return { warning: 'meta payload too large and was truncated' };
        }

        return JSON.parse(serialized);
    } catch (error) {
        return { warning: 'meta payload could not be serialized' };
    }
}

function matchesExcludedPattern(message) {
    if (!message) return false;
    return config.excludedPatterns.some(pattern => pattern.test(message));
}

function getMetaContent(name) {
    if (typeof document === 'undefined') return null;
    const element = document.querySelector(`meta[name="${name}"]`);
    if (!element) return null;
    return element.getAttribute('content') || null;
}

function getSessionFingerprint() {
    try {
        const source = typeof document !== 'undefined'
            ? document.cookie || (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown')
            : (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown');

        let hash = 0;
        for (let i = 0; i < source.length; i++) {
            hash = ((hash << 5) - hash) + source.charCodeAt(i);
            hash |= 0;
        }

        return `session-${Math.abs(hash).toString(36).slice(0, 10)}`;
    } catch (error) {
        return 'session-unknown';
    }
}

function getConnectionInfo() {
    if (typeof navigator === 'undefined' || !navigator.connection) {
        return null;
    }

    const { effectiveType, downlink, rtt, saveData } = navigator.connection;
    return {
        effectiveType: effectiveType || null,
        downlink: typeof downlink === 'number' ? downlink : null,
        rtt: typeof rtt === 'number' ? rtt : null,
        saveData: typeof saveData === 'boolean' ? saveData : null,
    };
}

function getDeviceInfo() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
        return null;
    }

    const { deviceMemory, hardwareConcurrency } = navigator;
    const screenInfo = typeof screen !== 'undefined' ? {
        width: screen.width || null,
        height: screen.height || null,
        pixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
    } : null;

    return {
        memory_gb: typeof deviceMemory === 'number' ? deviceMemory : null,
        cores: typeof hardwareConcurrency === 'number' ? hardwareConcurrency : null,
        screen: screenInfo,
    };
}

function buildPayload(error, options = {}) {
    const normalizedError = normalizeError(error);

    return {
        level: options.level || 'error',
        message: normalizedError.message,
        stack: normalizedError.stack,
        file: options.file || null,
        line: Number.isInteger(options.line) ? options.line : null,
        column: Number.isInteger(options.column) ? options.column : null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        app_version: getMetaContent('app-version'),
        client_timestamp: new Date().toISOString(),
        release: getMetaContent('release'),
        session_id: getSessionFingerprint(),
        navigator_online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        connection: getConnectionInfo(),
        device: getDeviceInfo(),
        meta: sanitizeMeta(options.meta),
    };
}

function isBot() {
    if (typeof navigator === 'undefined') return false;
    return /bot|crawler|spider|applebot|googlebot|bingbot|yandex|baidu|duckduck|semrush|ahref|facebookexternalhit|bytespider|headlesschrome|lighthouse|prerender/i.test(navigator.userAgent);
}

async function send(payload) {
    if (isBot()) {
        return;
    }

    if (matchesExcludedPattern(payload.message)
        || matchesExcludedPattern(payload.file)
        || matchesExcludedPattern(payload.stack)) {
        return;
    }

    if (!isAllowedToSend()) {
        return;
    }

    try {
        await fetch(config.endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
        });
    } catch (error) {
        console.error('Frontend error logging failed', error);
    }
}

function log(error, options = {}) {
    const payload = buildPayload(error, options);
    send(payload);
}

function registerGlobalHandlers() {
    if (typeof window === 'undefined' || globalHandlersRegistered) {
        return;
    }

    globalHandlersRegistered = true;

    window.addEventListener('error', (event) => {
        log(event.error || event.message || 'Unknown window error', {
            file: event.filename || null,
            line: Number.isInteger(event.lineno) ? event.lineno : null,
            column: Number.isInteger(event.colno) ? event.colno : null,
            meta: {
                source: 'window.error',
            },
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        log(event.reason || 'Unhandled promise rejection', {
            meta: {
                source: 'window.unhandledrejection',
            },
        });
    });
}

function init(options = {}) {
    config = {
        ...DEFAULT_CONFIG,
        ...options,
    };

    registerGlobalHandlers();

    if (typeof window !== 'undefined') {
        window.mtErrorLogging = {
            log,
        };
    }
}

export {
    init,
    log,
};
